import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiX } from "react-icons/fi";
import { CURRENCY, formatMoney } from "../constants/currency";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const POLL_MS = 3000;

function parsePositiveAmount(raw) {
  if (raw === undefined || raw === null) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  const n = parseFloat(s.replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export default function DepositForm({ compact = false, onClose }) {
  const { fetchUser } = useAuth();
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState("form");
  const [pendingTransactionId, setPendingTransactionId] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);

  const fetchUserRef = useRef(fetchUser);
  fetchUserRef.current = fetchUser;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const buttonAmountLabel = useMemo(() => {
    const n = parsePositiveAmount(amount);
    if (n === null) return null;
    return formatMoney(n);
  }, [amount]);

  useEffect(() => {
    if (phase !== "waiting" || !pendingTransactionId) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    let cancelled = false;
    let intervalId;

    const stop = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = undefined;
    };

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/payments/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const tx = data.transactions?.find(
          (t) => String(t._id) === String(pendingTransactionId)
        );
        if (!tx || tx.status === "pending") return;

        stop();

        if (tx.status === "completed") {
          toast.success("Payment confirmed! Your balance has been updated.");
          await fetchUserRef.current();
          setAmount("");
          setPhase("form");
          setPendingTransactionId(null);
          setPaymentUrl(null);
          onCloseRef.current?.();
          return;
        }

        const msg =
          tx.status === "expired"
            ? "Payment expired."
            : tx.status === "cancelled"
              ? "Payment cancelled."
              : "Payment failed.";
        toast.error(msg);
        setPhase("form");
        setPendingTransactionId(null);
        setPaymentUrl(null);
      } catch {
        /* next poll retries */
      }
    };

    intervalId = setInterval(poll, POLL_MS);
    void poll();
    return () => {
      cancelled = true;
      stop();
    };
  }, [phase, pendingTransactionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/payments/topup`,
        { amount: parseFloat(amount), currency: CURRENCY },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const url = response.data.paymentLink?.url;
      const txId = response.data.transactionId;

      if (url && txId) {
        window.open(url, "_blank", "noopener,noreferrer");
        setPaymentUrl(url);
        setPendingTransactionId(txId);
        setPhase("waiting");
      } else {
        toast.success("Payment link created. Please check your email or dashboard.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create payment link");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`w-full ${
        compact ? "max-w-2xl sm:min-w-[520px]" : "max-w-[647px]"
      } rounded-2xl border border-[#EBEBEB1C] bg-[#6060604F] backdrop-blur-[26.2px] p-6 shadow-[0_25px_50px_rgba(2,6,23,0.45)]`}
    >
      <div className="mb-5 flex items-center justify-between gap-3">
        <h1 className={`${compact ? "text-2xl" : "text-4xl"} font-light text-white`}>
          Deposit
        </h1>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            <FiX size={18} />
          </button>
        ) : null}
      </div>

      {phase === "waiting" ? (
        <div className="flex flex-col items-center justify-center gap-5 py-10">
          <div
            className="h-12 w-12 shrink-0 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500"
            aria-hidden
          />
          <p className="text-center text-base text-white/90">
            Waiting for payment confirmation…
          </p>
          {paymentUrl ? (
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-[#7B9CFF] underline decoration-white/20 underline-offset-2 transition hover:text-white hover:decoration-white/50"
            >
              Open payment page
            </a>
          ) : null}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm text-white">Amount</label>
            <input
              type="number"
              step="0.01"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Type Amount"
              className="rdp-input bg-[#04061640] border border-[#EBEBEB1C] py-4 text-white placeholder-[#FFFFFF78] font-light"
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full text-md md:text-xl items-center justify-between rounded-xl 
          bg-[#FFFFFF0F] px-4 py-3 text-left text-white
          shadow-[inset_0_1px_1px_#FFFFFF3B,0_2px_4px_#00000040,inset_0_-16px_28px_#FFFFFF26]
          transition-all duration-200
          hover:bg-[#FFFFFF14]
          disabled:opacity-50"
          >
            <span className="font-medium">{submitting ? "Processing..." : "Pay Securely"}</span>
            <span className="flex items-center gap-3">
              {buttonAmountLabel != null && (
                <span className="tabular-nums">{buttonAmountLabel}</span>
              )}
              <div className="w-[33px] h-[33px] bg-white rounded-full flex items-center justify-center shrink-0">
                <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0.75 16.75L8.75 8.75L0.75 0.75"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </span>
          </button>

          <p className="text-xs text-white/90">
            <b>Need Help?</b>{" "}
            <a
              href="https://t.me/FocusedStudio"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#7B9CFF] underline decoration-white/20 underline-offset-2 transition hover:text-white hover:decoration-white/50"
            >
              @FocusedStudio
            </a>{" "}
            on Telegram — we&apos;re here <b>24/7</b>.
          </p>
        </form>
      )}
    </div>
  );
}

